import { all, put, race, take, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import config from '@config'
import { getLocale, getToken, getCustomData } from '@global/selectors'

import { BLOCK_DATA_LOADED } from '../../../../../../../../../components/Workspace/components/HocBlock/constants'
import { blockSaveCustomData } from '../../../../../../../../../components/Workspace/components/HocBlock/actions'

import {
    PERSONS_END_USERS_TABLE_CHANGE,
    PERSONS_END_USERS_TABLE_CHANGE_REQUEST,
    PERSONS_END_USERS_TABLE_LOADING_TRUE,
    PERSONS_END_USERS_TABLE_LOADING_FALSE,
    PERSONS_END_USERS_TABLE_DATA_RECEIVED,
} from './constants'

import {
    personsEndUsersTableDataReceived,
    personsEndUsersTableChangeRequest,
    personsEndUsersTableLoadingTrue,
    personsEndUsersTableLoadingFalse,
} from './actions'

const PERSONS_END_USERS_TABLE_CHANGE_REQUEST_SUCCESS = (id) =>
    `${PERSONS_END_USERS_TABLE_CHANGE_REQUEST(id)}_${SUCCESS}`
const PERSONS_END_USERS_TABLE_CHANGE_REQUEST_FAILED = (id) => `${PERSONS_END_USERS_TABLE_CHANGE_REQUEST(id)}_${FAILED}`

function* onDataLoaded(_action) {
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.persons.key) return null

    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    yield put(
        personsEndUsersTableDataReceived(module, id, blockKey, {
            isLoading: false,
            data: _.get(_action, 'data.data[2].endUserBenefits', []),
            pagination: _.get(_action, 'data.data[2].query.pagination', {}),
            sorted: _.get(_action, 'data.data[2].query.sort.sortedColumns', []),
            filter: _.get(_action, 'data.data[2].query.filter', []),
        })
    )

    return null
}

function* personsEndUsersTableSaga(action) {
    const module = _.get(action, 'data.module', null)
    const blockKey = _.get(action, 'data.blockKey', null)
    const id = _.get(action, 'data.id', null)
    const level = _.get(action, 'data.level', null)
    const queryParams = _.get(action, 'data.queryParams', null)

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level) return null

    yield put(personsEndUsersTableLoadingTrue(module, id, blockKey, { isLoading: true }))

    const filters = _.reduce(
        _.get(queryParams, 'filtered'),
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
        ...filters,
    }

    yield put(personsEndUsersTableChangeRequest(module, blockKey, id, level, lang, token, qp))

    const response = yield race({
        success: take(PERSONS_END_USERS_TABLE_CHANGE_REQUEST_SUCCESS(id)),
        failed: take(PERSONS_END_USERS_TABLE_CHANGE_REQUEST_FAILED(id)),
    })

    if (!response.failed) {
        const data = _.map(_.get(response, 'success.payload.response.body.endUserBenefits'), (row) => ({ ...row }))
        const query = _.get(response, 'success.payload.response.body.query', [])
        const pagination = _.get(query, 'pagination', null)
        const filter = _.get(query, 'filter', null)
        const sorted = _.reduce(
            _.get(query, 'sort.sortedColumns'),
            (acc, val) => [
                ...acc,
                {
                    id: _.get(val, 'columm'), // TYPO FROM BACKEND
                    desc: _.get(val, 'direction') === 'desc',
                },
            ],
            []
        )

        const newCustomData = {
            data,
            pagination,
            sorted,
            filter,
            isLoading: false,
        }

        yield put(personsEndUsersTableDataReceived(module, id, blockKey, newCustomData))
    } else {
        yield put(personsEndUsersTableLoadingFalse(module, id, blockKey, { isLoading: false }))
    }

    return null
}

function* personsEndUsersChangeCustomData(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.persons.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, blockKey, id)
    const incomingChange = action?.data?.customData

    const newCustomData = {
        ...customData,
        endUsers: {
            ...(customData?.representatives ?? {}),
            ...incomingChange,
        },
    }

    yield put(blockSaveCustomData(module, id, blockKey, newCustomData))

    return null
}

export default function* endUsersSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(PERSONS_END_USERS_TABLE_LOADING_TRUE, personsEndUsersChangeCustomData),
        takeEvery(PERSONS_END_USERS_TABLE_LOADING_FALSE, personsEndUsersChangeCustomData),
        takeEvery(PERSONS_END_USERS_TABLE_DATA_RECEIVED, personsEndUsersChangeCustomData),
        takeLatest(PERSONS_END_USERS_TABLE_CHANGE, personsEndUsersTableSaga),
    ])
}
